/**
 * MeetGranulloIntelligence.jsx — Live Conversation Engine
 *
 * FOUNDER DIRECTIVE: This is the MOST IMPORTANT section on the site.
 * NOT a chatbot. NOT a dashboard. NOT a static output.
 *
 * This is observing a system thinking — in real-time conversation.
 * Multi-turn dialogue. Typewriter effect. Earned verdicts.
 *
 * 4 scenarios: Procurement, Benefits, Insurance, Subsidy
 * Auto-cycles. Hover = pause.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ArrowRight } from 'lucide-react';
import { GranulloButton } from '../ui/GranulloButton';
import { lensAnnotation, LENS_TYPES } from '../lens-v2/LensAnnotations';
import {
  useGranulloIntelligence,
  COLORS as SYS_COLORS,
} from '../../contexts/GranulloIntelligenceContext';

const C = {
  bg: 'var(--mi-bg, #F5F5F5)',
  text: 'var(--mi-text, #0A0A0A)',
  dim: 'var(--mi-dim, #555555)',
  faint: 'var(--mi-faint, #999999)',
  system: '#B8D7F0',
  cyan: '#B8D7F0',
  blocked: '#EFD6B8',
  allowed: '#C7E3D4',
};

/* ═══════════════════════ CONVERSATION SCENARIOS ═══════════════════════ */
const SCENARIOS = [
  {
    id: 'procurement',
    label: 'Procurement',
    context: 'Corporate Procurement — PO #38291',
    prevention: '$45000 vendor payment, verified suppliers only, delivery confirmation required',
    messages: [
      { role: 'user', text: "We've paid this vendor before. The PO is approved. Why is this stuck?" },
      { role: 'thinking', text: 'Checking rules...' },
      { role: 'granullo', text: 'Vendor identity verified.' },
      { role: 'granullo', text: 'Purchase order matched — $45,000.' },
      { role: 'granullo', text: 'Budget allocation confirmed.' },
      { role: 'granullo', text: 'Checking delivery confirmation...' },
      { role: 'granullo', text: "Delivery has not been confirmed.", fail: true },
      { role: 'user', text: "But the invoice is already approved?" },
      { role: 'granullo', text: 'Approval allows the payment.' },
      { role: 'granullo', text: 'Confirmation releases the payment.' },
      { role: 'granullo', text: "Payment requires confirmation from the receiving warehouse." },
      { role: 'verdict', text: "This can't go through yet.", blocked: true },
    ],
  },
  {
    id: 'benefits',
    label: 'Benefits',
    context: 'Employee Wellness — Card #7291',
    prevention: '$200 employee wellness benefit, gym only, no restaurants',
    messages: [
      { role: 'user', text: "I tried to pay at a restaurant with my benefits card. It got declined." },
      { role: 'thinking', text: 'Checking rules...' },
      { role: 'granullo', text: 'Employee identity confirmed.' },
      { role: 'granullo', text: 'Benefit balance available — $340 remaining.' },
      { role: 'granullo', text: 'Checking merchant category...' },
      { role: 'granullo', text: "Restaurant is not in the permitted list.", fail: true },
      { role: 'user', text: "But I have enough balance. Why was it rejected?" },
      { role: 'granullo', text: "Balance doesn't determine where you can spend." },
      { role: 'granullo', text: 'This card is restricted to gym, pharmacy, and wellness merchants.' },
      { role: 'granullo', text: 'Restaurants are not a permitted category.' },
      { role: 'verdict', text: "This doesn't match what the money is for.", blocked: true },
    ],
  },
  {
    id: 'insurance',
    label: 'Insurance',
    context: 'Home Repair Claim — #4829',
    prevention: '$12400 insurance payout for home repairs, licensed contractor verified',
    messages: [
      { role: 'user', text: "I filed a home repair claim three weeks ago. It got approved. Where's my money?" },
      { role: 'thinking', text: 'Checking rules...' },
      { role: 'granullo', text: 'Claim documentation complete.' },
      { role: 'granullo', text: 'Policy limit verified — $12,400 within coverage.' },
      { role: 'granullo', text: 'Checking adjuster assessment...' },
      { role: 'granullo', text: 'Independent adjuster assessment confirmed.' },
      { role: 'granullo', text: 'Contractor is licensed and in the approved registry.' },
      { role: 'granullo', text: 'Purpose matches home repair claim scope.' },
      { role: 'user', text: "So everything checks out?" },
      { role: 'granullo', text: 'Every check has been verified independently.' },
      { role: 'granullo', text: 'Releasing $12,400 to licensed contractor.' },
      { role: 'verdict', text: 'Allowed. All checks passed.', blocked: false },
    ],
  },
  {
    id: 'subsidy',
    label: 'Subsidy',
    context: 'Agricultural Subsidy — Beneficiary #1847',
    prevention: 'INR 10000 agricultural subsidy, verified suppliers only',
    messages: [
      { role: 'user', text: "My subsidy is approved but I can't buy seeds from my local shop. Why?" },
      { role: 'thinking', text: 'Checking rules...' },
      { role: 'granullo', text: 'Beneficiary eligibility verified.' },
      { role: 'granullo', text: 'Subsidy allocation active — \u20B910,000.' },
      { role: 'granullo', text: 'Checking vendor registry...' },
      { role: 'granullo', text: "This vendor is not in the verified supplier registry.", fail: true },
      { role: 'user', text: "But I've bought from them for years." },
      { role: 'granullo', text: 'Purchase history does not establish verification.' },
      { role: 'granullo', text: 'Subsidized purchases must be from registered agricultural suppliers.' },
      { role: 'granullo', text: 'This vendor has not completed the registration process.' },
      { role: 'verdict', text: "This can't go through yet.", blocked: true },
    ],
  },
];

/* ═══════════════════════ TYPEWRITER HOOK ═══════════════════════ */
function useTypewriter(text, active, speed = 22) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!active) { setDisplayed(''); setDone(false); return; }
    setDisplayed('');
    setDone(false);
    let i = 0;
    const id = setInterval(() => {
      i++;
      if (i >= text.length) {
        setDisplayed(text);
        setDone(true);
        clearInterval(id);
      } else {
        setDisplayed(text.slice(0, i));
      }
    }, speed);
    return () => clearInterval(id);
  }, [text, active, speed]);

  return { displayed, done };
}

/* ═══════════════════════ MESSAGE BUBBLE ═══════════════════════ */
function MessageBubble({ msg, active, onDone, isLast }) {
  const isUser = msg.role === 'user';
  const isThinking = msg.role === 'thinking';
  const isVerdict = msg.role === 'verdict';
  const isFail = msg.fail;
  const speed = isVerdict ? 28 : isThinking ? 35 : isUser ? 16 : 20;
  const { displayed, done } = useTypewriter(msg.text, active, speed);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => { if (done) onDoneRef.current?.(); }, [done]);

  if (!active) return null;

  if (isVerdict) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex justify-start"
      >
        <div className="max-w-[85%]">
          {/* Verdict explanation */}
          <div className="px-4 py-3 rounded-xl mb-2"
            style={{ background: 'rgba(var(--mi-text-rgb),0.02)', border: '1px solid rgba(var(--mi-text-rgb),0.05)' }}>
            <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(var(--mi-text-rgb),0.6)' }} data-testid="verdict-explanation">
              {displayed}
              {!done && <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.5, repeat: Infinity }} style={{ color: C.system }}>|</motion.span>}
            </p>
          </div>
          {/* Verdict badge */}
          {done && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25, delay: 0.3 }}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg"
              style={{
                background: msg.blocked !== false ? `${C.blocked}0A` : `${C.allowed}0A`,
                border: `1px solid ${msg.blocked !== false ? `${C.blocked}20` : `${C.allowed}20`}`,
                boxShadow: `0 0 16px ${msg.blocked !== false ? `${C.blocked}08` : `${C.allowed}08`}`,
              }}
              data-testid="conversation-verdict"
            >
              <Shield size={13} style={{ color: msg.blocked !== false ? C.blocked : C.allowed }} />
              <span className="text-[12px] font-mono font-semibold" style={{ color: msg.blocked !== false ? C.blocked : C.allowed }}>
                {msg.blocked !== false ? 'BLOCKED' : 'ALLOWED'}
              </span>
            </motion.div>
          )}
        </div>
      </motion.div>
    );
  }

  if (isThinking) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
        <motion.div className="px-4 py-2.5 rounded-xl"
          style={{ background: 'rgba(184,215,240,0.04)', border: '1px solid rgba(184,215,240,0.08)' }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: done ? 0 : Infinity }}>
          <span className="text-[12px] font-mono" style={{ color: `${C.system}60` }} data-testid="thinking-state">
            {displayed}
            {!done && <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.4, repeat: Infinity }} style={{ color: C.system }}>...</motion.span>}
          </span>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`max-w-[85%] px-4 py-2.5 rounded-xl ${isUser ? 'rounded-br-md' : 'rounded-bl-md'}`}
        style={{
          background: isUser ? 'rgba(var(--mi-text-rgb),0.04)' : isFail ? `${C.blocked}06` : 'rgba(184,215,240,0.04)',
          border: `1px solid ${isUser ? 'rgba(var(--mi-text-rgb),0.06)' : isFail ? `${C.blocked}15` : 'rgba(184,215,240,0.08)'}`,
        }}>
        <p className="text-[13px] leading-relaxed"
          style={{ color: isUser ? 'rgba(var(--mi-text-rgb),0.55)' : isFail ? `${C.blocked}cc` : 'rgba(var(--mi-text-rgb),0.7)' }}
          data-testid={isUser ? 'user-message' : 'granullo-message'}>
          {displayed}
          {!done && <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.5, repeat: Infinity }} style={{ color: isUser ? 'rgba(var(--mi-text-rgb),0.3)' : C.system }}>|</motion.span>}
        </p>
      </div>
    </motion.div>
  );
}

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════ */
export default function MeetGranulloIntelligence() {
  const { injectProgram } = useGranulloIntelligence();
  const [activeIdx, setActiveIdx] = useState(0);
  const [visibleMsgs, setVisibleMsgs] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [conversationDone, setConversationDone] = useState(false);
  const timersRef = useRef([]);
  const chatRef = useRef(null);

  const scenario = SCENARIOS[activeIdx];

  const clear = useCallback(() => { timersRef.current.forEach(clearTimeout); timersRef.current = []; }, []);

  /* Start conversation — reveal messages with delays */
  useEffect(() => {
    clear();
    setVisibleMsgs(0);
    setConversationDone(false);

    /* Reveal first message immediately */
    const t = (fn, ms) => { timersRef.current.push(setTimeout(fn, ms)); };
    t(() => setVisibleMsgs(1), 300);

    return clear;
  }, [activeIdx, clear]);

  /* Handle message completion — advance to next with appropriate delay */
  const handleMsgDone = useCallback((msgIdx) => {
    if (msgIdx + 1 >= scenario.messages.length) {
      setConversationDone(true);
      return;
    }
    const nextMsg = scenario.messages[msgIdx + 1];
    /* Delay based on next message type */
    let delay = 500;
    if (nextMsg.role === 'user') delay = 900;
    if (nextMsg.role === 'thinking') delay = 400;
    if (nextMsg.role === 'verdict') delay = 800;
    if (nextMsg.fail) delay = 700;

    const timer = setTimeout(() => {
      setVisibleMsgs(msgIdx + 2);
      /* Auto-scroll chat */
      if (chatRef.current) {
        setTimeout(() => chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' }), 50);
      }
    }, delay);
    timersRef.current.push(timer);
  }, [scenario.messages]);

  /* Auto-advance to next scenario */
  useEffect(() => {
    if (!conversationDone || isHovered) return;
    const timer = setTimeout(() => {
      setActiveIdx(i => (i + 1) % SCENARIOS.length);
    }, 3000);
    return () => clearTimeout(timer);
  }, [conversationDone, isHovered]);

  const handleTryThis = useCallback(() => {
    if (!scenario.prevention) return;
    injectProgram(scenario.prevention, 'intelligence');
    const el = document.getElementById('execution-intercept');
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 72;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, [scenario.prevention, injectProgram]);

  return (
    <section className="g-meet-intelligence py-16 sm:py-28 relative" style={{ background: C.bg, minHeight: '720px' }} data-testid="meet-granullo-intelligence"
      {...lensAnnotation({
        id: 'granullo-intelligence',
        type: 'scenario-card',
        title: 'Granullo Intelligence — Live Reasoning Surface',
        meaning: 'Observing a system thinking in real-time. Multi-turn dialogue where Granullo Intelligence analyzes procurement, benefits, insurance, and subsidy scenarios. Not a chatbot — a structural reasoning engine.',
        actions: ['Ask a question', 'Inspect reasoning chain', 'View consequence path'],
        relations: ['rule-pack', 'pressure-card', 'misuse-future', 'structural-fix'],
        state: 'intelligence-active',
        priority: 'high',
      })}
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">

        {/* Header — Attio-grade editorial hero */}
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12 sm:mb-16">
          <div className="flex items-center gap-2 mb-5">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.2em]"
              style={{ background: 'rgba(var(--mi-text-rgb),0.04)', border: '1px solid rgba(var(--mi-text-rgb),0.08)', color: C.dim }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#22C55E', boxShadow: '0 0 8px #22C55E' }} />
              Live · reasoning on air
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: C.faint }}>
              Granullo Intelligence
            </span>
          </div>
          <h2 className="text-[34px] sm:text-[52px] lg:text-[64px] font-semibold leading-[1.02] tracking-[-0.028em] max-w-[900px]"
            style={{ color: C.text, fontFamily: '"Source Serif 4", Georgia, serif' }}
            data-testid="meet-intelligence-heading">
            Ask{' '}
            <span style={{ color: 'var(--g-editorial-red, #CD2200)' }}>Granullo Intelligence</span>.
          </h2>
          <p className="text-[15px] sm:text-[17px] mt-5 max-w-[640px] leading-[1.55]" style={{ color: C.dim }}>
            Granullo Intelligence is a structural reasoning engine. It checks every rule <em>before</em> the payment moves,
            explains exactly why, and remembers what it learned. No hallucinations. No black box.
          </p>
          <p className="text-[13px] mt-3 -rotate-1 inline-block" style={{ fontFamily: '"Kalam", cursive', fontSize: 20, color: 'var(--g-editorial-red, #CD2200)' }}>
            watch a real decision happen — live.
          </p>
        </motion.div>

        {/* Two Column Layout — locked height shell */}
        <div className="grid lg:grid-cols-[1fr,1.4fr] gap-10 lg:gap-16" style={{ minHeight: '520px' }}>

          {/* Left: Context + Capabilities */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <p className="text-[15px] leading-relaxed mb-8" style={{ color: C.dim }}>
              Before any money moves, Granullo checks every rule. Not after. Not alongside. Before.
            </p>

            {/* Scenario Selector — Attio-grade pill tabs with outcome microtags */}
            <div className="mb-8">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] mb-3" style={{ color: C.faint }}>
                Pick a scenario
              </p>
              <div className="flex flex-wrap gap-2" data-testid="conversation-tabs">
                {SCENARIOS.map((s, i) => {
                  const active = i === activeIdx;
                  const verdict = s.messages.find(m => m.role === 'verdict');
                  const outcomeTag = verdict?.blocked === false ? 'allowed' : 'blocked';
                  const outcomeColor = verdict?.blocked === false ? '#22C55E' : 'var(--g-editorial-red, #CD2200)';
                  return (
                    <motion.button
                      key={s.id}
                      onClick={() => setActiveIdx(i)}
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[12px] font-semibold cursor-pointer"
                      style={{
                        background: active ? 'var(--g-editorial-red, #CD2200)' : 'rgba(var(--mi-text-rgb),0.03)',
                        color: active ? '#fff' : C.text,
                        border: `1px solid ${active ? 'var(--g-editorial-red, #CD2200)' : 'rgba(var(--mi-text-rgb),0.08)'}`,
                        boxShadow: active ? '0 0 20px rgba(205,34,0,0.30)' : 'none',
                        transition: 'all 220ms ease',
                      }}
                      data-testid={`conversation-tab-${s.id}`}
                    >
                      {s.label}
                      <span
                        className="inline-flex items-center text-[9px] font-mono font-semibold uppercase tracking-[0.14em] pl-2 py-0.5"
                        style={{
                          color: active ? '#fff' : outcomeColor,
                          borderLeft: `1px solid ${active ? 'rgba(255,255,255,0.2)' : `${outcomeColor}40`}`,
                          opacity: active ? 0.9 : 1,
                        }}
                      >
                        {outcomeTag === 'allowed' ? '✓ allowed' : '✕ blocked'}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* What it does */}
            <div className="space-y-3 mb-8">
              {[
                'Checks every rule before money moves',
                'Explains exactly why something is blocked',
                'Makes non-compliance impossible — not just detectable',
              ].map((line, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-1 h-1 rounded-full mt-2 shrink-0" style={{ background: C.system }} />
                  <span className="text-[13px] leading-relaxed" style={{ color: C.dim }}>{line}</span>
                </div>
              ))}
            </div>

            <div className="rounded-lg p-4" style={{ background: 'rgba(var(--mi-text-rgb),0.015)', border: '1px solid rgba(var(--mi-text-rgb),0.04)' }}
              data-testid="intelligence-differentiator">
              <p className="text-[11px] font-mono" style={{ color: C.faint }}>
                If it is not allowed, it does not happen.
              </p>
            </div>

            {/* ═══ HUMAN PROFILE OBJECTS — Attio-grade context ═══ */}
            <div className="mt-8 space-y-3" data-testid="context-profiles">
              {[
                { role: 'Sponsor', name: scenario.id === 'procurement' ? 'Capital Bureau' : scenario.id === 'benefits' ? 'HR Department' : scenario.id === 'insurance' ? 'Claims Office' : 'Agricultural Board', status: 'Active' },
                { role: 'Beneficiary', name: scenario.id === 'procurement' ? 'Verified Vendor #391' : scenario.id === 'benefits' ? 'Employee #7291' : scenario.id === 'insurance' ? 'Policy Holder #4829' : 'Farmer #1847', status: 'Verified' },
              ].map((profile, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg" style={{ background: 'rgba(var(--mi-text-rgb),0.02)', border: '1px solid rgba(var(--mi-text-rgb),0.04)' }}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-mono font-semibold"
                    style={{ background: 'rgba(var(--mi-text-rgb),0.06)', color: C.dim }}>
                    {profile.name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium truncate" style={{ color: C.text }}>{profile.name}</p>
                    <p className="text-[9px] font-mono uppercase tracking-wider" style={{ color: C.faint }}>{profile.role}</p>
                  </div>
                  <span className="text-[8px] font-mono uppercase px-1.5 py-0.5 rounded-full"
                    style={{ background: profile.status === 'Active' ? 'rgba(34,197,94,0.08)' : 'rgba(184,215,240,0.12)', color: profile.status === 'Active' ? '#22C55E' : C.system }}>
                    {profile.status}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: Live Conversation Panel */}
          <div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <AnimatePresence mode="wait">
              <motion.div key={scenario.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl overflow-hidden relative"
                style={{
                  background: 'var(--shell-surface, #fff)',
                  border: '1px solid rgba(var(--mi-text-rgb),0.08)',
                  boxShadow: '0 30px 80px rgba(0,0,0,0.08), 0 2px 0 rgba(255,255,255,0.8) inset',
                }}
                data-testid="conversation-panel"
              >
                {/* macOS-style window chrome + live status */}
                <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(var(--mi-text-rgb),0.04)' }}>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#FF5F57' }} />
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#FEBC2E' }} />
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#28C840' }} />
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <motion.div className="w-1.5 h-1.5 rounded-full"
                        style={{ background: conversationDone ? (scenario.messages.find(m => m.role === 'verdict' && m.blocked === false) ? C.allowed : C.blocked) : C.system }}
                        animate={conversationDone ? { opacity: 0.9 } : { opacity: [0.3, 0.9, 0.3] }}
                        transition={{ duration: 1.5, repeat: conversationDone ? 0 : Infinity }} />
                      <span className="text-[10px] font-mono" style={{ color: C.faint }}>{scenario.context}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded"
                      style={{
                        color: conversationDone ? '#22C55E' : C.dim,
                        background: conversationDone ? 'rgba(34,197,94,0.08)' : 'rgba(var(--mi-text-rgb),0.03)',
                        border: `1px solid ${conversationDone ? 'rgba(34,197,94,0.25)' : 'rgba(var(--mi-text-rgb),0.06)'}`,
                      }}>
                      {conversationDone ? '✓ complete' : visibleMsgs > 0 ? 'evaluating' : 'ready'}
                    </span>
                  </div>
                </div>

                {/* Chat Messages */}
                <div ref={chatRef} className="p-5 sm:p-6 space-y-3 overflow-y-auto" style={{ maxHeight: '440px', minHeight: '360px' }}
                  data-testid="conversation-messages">
                  {scenario.messages.map((msg, i) => (
                    <MessageBubble
                      key={`${scenario.id}-${i}`}
                      msg={msg}
                      active={i < visibleMsgs}
                      onDone={() => handleMsgDone(i)}
                      isLast={i === scenario.messages.length - 1}
                    />
                  ))}
                </div>

                {/* CTA — Ask-style input composer, premium */}
                <div className="px-5 py-4" style={{ borderTop: '1px solid rgba(var(--mi-text-rgb),0.04)', background: 'rgba(var(--mi-text-rgb),0.015)' }}>
                  <div
                    className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl"
                    style={{
                      background: 'var(--shell-bg, #fff)',
                      border: '1px solid rgba(var(--mi-text-rgb),0.08)',
                      boxShadow: '0 2px 0 rgba(var(--mi-text-rgb),0.03)',
                    }}
                    data-testid="ask-granullo-input"
                  >
                    <Shield size={13} style={{ color: 'var(--g-editorial-red, #CD2200)', flexShrink: 0 }} />
                    <span className="text-[12.5px] flex-1" style={{ color: C.faint }}>
                      {conversationDone ? 'Ask another question…' : 'What should this money prove?'}
                    </span>
                    <kbd className="hidden sm:inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.1em] px-1.5 py-0.5 rounded"
                      style={{ color: C.faint, background: 'rgba(var(--mi-text-rgb),0.04)', border: '1px solid rgba(var(--mi-text-rgb),0.06)' }}>
                      ⌘ K
                    </kbd>
                  </div>
                  <AnimatePresence>
                    {conversationDone && (
                      <motion.button
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.3 }}
                        onClick={handleTryThis}
                        className="flex items-center gap-2 text-[12px] font-semibold mt-3 transition-colors"
                        style={{ color: 'var(--g-editorial-red, #CD2200)', cursor: 'pointer', background: 'none', border: 'none' }}
                        data-testid="conversation-try-cta"
                      >
                        Try this with your own rules <ArrowRight size={12} />
                      </motion.button>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* ═══ CAPABILITY PILLAR STRIP — Attio-grade proof bar ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.5 }}
          className="mt-16"
          data-testid="intelligence-pillars"
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] mb-4" style={{ color: C.faint }}>
            Why this is not a chatbot
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { title: 'Scoped',         desc: 'Every answer is bounded by your rules. No inventing facts.',           icon: '◼' },
              { title: 'Auditable',      desc: 'Every decision shows its full reasoning chain, not just a verdict.',   icon: '⟐' },
              { title: 'Real-time',      desc: 'Checks fire before money moves, not after it has been reconciled.',    icon: '⏱' },
              { title: 'Memory-aware',   desc: 'Every decision writes a Twin. The next one inherits what was learned.', icon: '✦' },
            ].map((p) => (
              <motion.div
                key={p.title}
                whileHover={{ y: -2 }}
                className="relative p-4 rounded-xl overflow-hidden"
                style={{
                  background: 'var(--shell-surface, #fff)',
                  border: '1px solid rgba(var(--mi-text-rgb),0.08)',
                  transition: 'all 240ms ease',
                }}
              >
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'var(--g-editorial-red, #CD2200)', opacity: 0.85 }} />
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md mb-3 font-mono text-[12px]"
                  style={{ background: 'rgba(205,34,0,0.08)', border: '1px solid rgba(205,34,0,0.3)', color: 'var(--g-editorial-red, #CD2200)' }}>
                  {p.icon}
                </span>
                <p className="text-[13.5px] font-semibold" style={{ color: C.text, letterSpacing: '-0.01em' }}>
                  {p.title}
                </p>
                <p className="text-[11.5px] leading-[1.5] mt-1" style={{ color: C.dim }}>
                  {p.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ═══ FLAGSHIP CTA — premium action panel ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 rounded-2xl p-6 sm:p-8 grid md:grid-cols-[1fr_auto] md:items-center gap-6 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(205,34,0,0.04), var(--shell-surface, #fff))',
            border: '1px solid rgba(205,34,0,0.20)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.06), 0 1px 0 rgba(255,255,255,0.7) inset',
          }}
          data-testid="intelligence-cta-block"
        >
          {/* Ambient glow */}
          <div
            aria-hidden
            className="absolute -top-24 -right-24 w-[340px] h-[340px] rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(205,34,0,0.10) 0%, transparent 60%)', filter: 'blur(24px)' }}
          />
          <div className="relative">
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] mb-2" style={{ color: 'var(--g-editorial-red, #CD2200)' }}>
              Explore Granullo Intelligence
            </p>
            <h3 className="text-[22px] sm:text-[28px] font-semibold leading-[1.15] tracking-[-0.02em] mb-2"
              style={{ color: C.text, fontFamily: '"Source Serif 4", Georgia, serif' }}>
              Bring your own rules. Watch them become decisions.
            </h3>
            <p className="text-[13px] sm:text-[14px] leading-[1.6] max-w-[560px]" style={{ color: C.dim }}>
              Co-create safer programs, investigate blocked outcomes, and simulate misuse before money moves.
              Full reasoning chain, full audit trail, zero black box.
            </p>
          </div>
          <div className="relative flex flex-col gap-2 items-start md:items-end">
            <GranulloButton
              variant="destructive"
              size="lg"
              as="a"
              href="/ask-intelligence"
              icon={ArrowRight}
              testId="intelligence-cta-button"
              className="gb-pill"
            >
              Ask Granullo Intelligence
            </GranulloButton>
            <GranulloButton
              variant="link"
              size="sm"
              as="a"
              href="/intelligence"
              icon={ArrowRight}
              testId="intelligence-page-cta"
            >
              How it works
            </GranulloButton>
          </div>
        </motion.div>

        {/* ═══ TRUST PROOF STRIP — final social-proof note ═══ */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-20px' }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[10.5px] font-mono uppercase tracking-[0.18em]"
          style={{ color: C.faint }}
          data-testid="intelligence-proof-strip"
        >
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#22C55E', boxShadow: '0 0 6px rgba(34,197,94,0.5)' }} />
            <span style={{ color: C.dim }}>Used by</span>
            <span style={{ color: C.text, fontWeight: 600 }}>Foundations · Trade Banks · CSR Boards</span>
          </span>
          <span style={{ color: `${C.faint}66` }}>·</span>
          <span className="inline-flex items-center gap-1.5">
            <span style={{ color: C.dim }}>This week</span>
            <span style={{ color: C.text, fontWeight: 600 }}>3,247 evaluators</span>
            <span style={{ color: C.dim }}>on air</span>
          </span>
          <span style={{ color: `${C.faint}66` }}>·</span>
          <span className="inline-flex items-center gap-1.5">
            <span style={{ color: C.dim }}>Every decision</span>
            <span style={{ color: 'var(--g-editorial-red, #CD2200)', fontWeight: 600 }}>writes a Twin</span>
          </span>
        </motion.div>
      </div>
    </section>
  );
}
